import { cn } from "cn";
import { X } from "lucide-react";
import type * as React from "react";
import { useId } from "react";

// A coach mark: one stop of a short guided tour ("1 of 4"), pointing at a
// control on the page. The panel colour with the pop shadow, a small step
// label in the brand ink, a 15px title, one or two lines of body, then the
// actions (Next / Done in ink, Skip tour quiet) and a close button.
//
// As a card next to its target from 640px (the caller positions it with
// `style`; `arrow` points at the target), docked at the bottom on phones
// (layout "dock", full width with the 16px gutter). Positioning, scrolling
// the target into view and its highlight stay with the caller.

export type CoachMarkArrow = "top" | "bottom" | "none";

export interface CoachMarkProps {
  /** 0-based stop and the number of stops. */
  step: number;
  total: number;
  /** "1 of 4". */
  stepLabel: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  /** Next on every stop but the last; Done (or the stop's own action) there. */
  primaryLabel: string;
  onPrimary: () => void;
  /** Skip the rest of the tour. Hidden on the last stop. */
  skipLabel?: string;
  onSkip?: () => void;
  closeLabel: string;
  onClose: () => void;
  /** "card": floating next to its target. "dock": pinned to the bottom. */
  layout?: "card" | "dock";
  /** Card only: which edge the arrow is on, and its left offset in px. */
  arrow?: CoachMarkArrow;
  arrowOffset?: number;
  style?: React.CSSProperties;
  className?: string;
  testId?: string;
}

const ACTION =
  "inline-flex h-11 shrink-0 items-center justify-center rounded-full px-4 text-[13.5px] font-semibold transition-[background-color,opacity] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand lg:h-9 lg:px-3.5";

export function CoachMark({
  step,
  total,
  stepLabel,
  title,
  children,
  primaryLabel,
  onPrimary,
  skipLabel,
  onSkip,
  closeLabel,
  onClose,
  layout = "card",
  arrow = "none",
  arrowOffset = 32,
  style,
  className,
  testId,
}: CoachMarkProps): React.JSX.Element {
  const titleId = useId();
  const bodyId = useId();
  const last = step >= total - 1;
  return (
    <div
      aria-describedby={children ? bodyId : undefined}
      aria-labelledby={titleId}
      className={cn(
        "relative z-50 rounded-[16px] bg-v3-panel p-4 font-v3 text-v3-text shadow-v3-pop",
        layout === "dock"
          ? "fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))]"
          : "w-[min(340px,calc(100vw-32px))]",
        className,
      )}
      data-layout={layout}
      data-slot="coach-mark"
      data-step={step}
      data-testid={testId}
      role="dialog"
      style={style}
    >
      {layout === "card" && arrow !== "none" && (
        <span
          aria-hidden
          className={cn(
            "absolute size-3 rotate-45 bg-v3-panel",
            arrow === "top"
              ? "-top-1.5 shadow-[-1px_-1px_0_var(--v3-border)]"
              : "-bottom-1.5 shadow-[1px_1px_0_var(--v3-border)]",
          )}
          data-arrow={arrow}
          style={{ left: arrowOffset - 6 }}
        />
      )}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold tabular-nums text-v3-brand-strong">
            {stepLabel}
          </p>
          <p
            className="mt-0.5 text-[15px] font-semibold leading-5 tracking-[-0.01em]"
            id={titleId}
          >
            {title}
          </p>
        </div>
        <button
          aria-label={closeLabel}
          className="-mr-2 -mt-2 inline-flex size-11 shrink-0 items-center justify-center rounded-full text-v3-text-3 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand lg:size-8"
          title={closeLabel}
          type="button"
          onClick={onClose}
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {children && (
        <div
          className="mt-1.5 text-[13.5px] leading-[1.45] text-v3-text-2"
          id={bodyId}
        >
          {children}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <span aria-hidden className="mr-auto flex gap-1">
          {Array.from({ length: total }, (_, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed stops.
              key={index}
              className={cn(
                "h-1.5 rounded-full transition-[width,background-color]",
                index === step ? "w-4 bg-v3-ink" : "w-1.5 bg-v3-border-strong",
              )}
            />
          ))}
        </span>
        {!last && skipLabel && onSkip && (
          <button
            className={cn(
              ACTION,
              "text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text",
            )}
            data-testid={testId ? `${testId}-skip` : undefined}
            type="button"
            onClick={onSkip}
          >
            {skipLabel}
          </button>
        )}
        <button
          className={cn(ACTION, "bg-v3-ink text-v3-on-ink hover:opacity-90")}
          data-testid={testId ? `${testId}-next` : undefined}
          type="button"
          onClick={onPrimary}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

// Where a card goes for a target: below it when there is room, else above;
// its left edge follows the target, kept inside the viewport (16px gutter).
// Returns the card's fixed position and the arrow's offset in the card.
export const placeCoachMark = ({
  target,
  card,
  viewport,
  gap = 12,
  gutter = 16,
}: {
  target: { top: number; left: number; width: number; height: number };
  card: { width: number; height: number };
  viewport: { width: number; height: number };
  gap?: number;
  gutter?: number;
}): {
  top: number;
  left: number;
  arrow: CoachMarkArrow;
  arrowOffset: number;
} => {
  const below = target.top + target.height + gap;
  const fitsBelow = below + card.height <= viewport.height - gutter;
  const above = target.top - gap - card.height;
  const arrow: CoachMarkArrow = fitsBelow || above < gutter ? "top" : "bottom";
  const top =
    arrow === "top"
      ? Math.min(
          below,
          Math.max(gutter, viewport.height - gutter - card.height),
        )
      : above;
  const maxLeft = Math.max(gutter, viewport.width - gutter - card.width);
  const left = Math.min(Math.max(target.left, gutter), maxLeft);
  const centre = target.left + Math.min(target.width, 64) / 2;
  const arrowOffset = Math.min(
    Math.max(centre - left, 20),
    Math.max(20, card.width - 20),
  );
  return { top, left, arrow, arrowOffset };
};
